import dayjs, { Dayjs } from 'dayjs';
import { ProductionOrder, TransactionStatus } from '../../../types/ProductionOrder';

export const DAY_WIDTH = 40; // default px per day column (Day zoom)
export const ROW_HEIGHT = 40; // px per order row
export const LABEL_WIDTH = 220; // px for the left (order) column

export type Zoom = 'day' | 'week' | 'month';

// px per day column for each zoom level
export const ZOOM_DAY_WIDTH: Record<Zoom, number> = {
  day: 40,
  week: 16,
  month: 6,
};

// Hex colours aligned with the app's <StatusTag> antd colour names so the board
// reads the same as the rest of the UI.
export const STATUS_COLOR: Record<TransactionStatus, string> = {
  DRAFT: '#bfbfbf',
  CONFIRM: '#faad14', // gold
  APPROVED: '#52c41a', // green
  WAITING: '#fa8c16', // orange
  AVAILABLE: '#13c2c2', // cyan
  READY: '#13c2c2', // cyan
  ISSUED: '#722ed1', // purple
  IN_TRANSIT: '#1677ff', // blue
  DONE: '#2f54eb', // geekblue
  CANCEL: '#f5222d', // red
};

export function statusColor(status?: string): string {
  if (!status) return '#bfbfbf';
  return STATUS_COLOR[status as TransactionStatus] ?? '#bfbfbf';
}

export type ColorMode = 'status' | 'section' | 'process';

// distinct palette for the "color by section/stage" mode (assigned deterministically
// per section code — the Section is effectively the production stage here).
const SECTION_PALETTE = [
  '#1677ff', '#52c41a', '#faad14', '#722ed1', '#13c2c2', '#eb2f96',
  '#fa8c16', '#2f54eb', '#a0d911', '#c41d7f', '#08979c', '#d48806',
];

export function sectionColorFor(code?: string | null): string {
  if (!code) return '#bfbfbf';
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return SECTION_PALETTE[h % SECTION_PALETTE.length];
}

/** Whole days between two dates (b - a), 0 if either is missing. */
export function dayDiff(a: Dayjs | string | null, b: Dayjs | string | null): number {
  if (!a || !b) return 0;
  return dayjs(b).startOf('day').diff(dayjs(a).startOf('day'), 'day');
}

export interface BarGeometry {
  left: number;
  width: number;
  visible: boolean;
}

/**
 * Position a bar (start..end) within a visible window (rangeStart..rangeEnd).
 * Bars are inclusive of the end day and clamped to the window.
 */
export function barGeometry(
  start: string | null,
  end: string | null,
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  dayWidth: number = DAY_WIDTH,
): BarGeometry {
  if (!start && !end) return { left: 0, width: 0, visible: false };
  const s = dayjs(start ?? end).startOf('day');
  const e = dayjs(end ?? start).startOf('day');
  const winStart = rangeStart.startOf('day');
  const winEnd = rangeEnd.startOf('day');

  // fully outside the window
  if (e.isBefore(winStart) || s.isAfter(winEnd)) {
    return { left: 0, width: 0, visible: false };
  }
  const clampedStart = s.isBefore(winStart) ? winStart : s;
  const clampedEnd = e.isAfter(winEnd) ? winEnd : e;

  const left = dayDiff(winStart, clampedStart) * dayWidth;
  const width = (dayDiff(clampedStart, clampedEnd) + 1) * dayWidth;
  return { left, width: Math.max(width, dayWidth * 0.5), visible: true };
}

/** X offset (px) of "today" within the window, or -1 if today is outside it. */
export function todayOffset(
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  dayWidth: number = DAY_WIDTH,
): number {
  const today = dayjs().startOf('day');
  if (today.isBefore(rangeStart.startOf('day')) || today.isAfter(rangeEnd.startOf('day'))) {
    return -1;
  }
  return dayDiff(rangeStart, today) * dayWidth + dayWidth / 2;
}

/** List of day columns spanning [rangeStart, rangeEnd] inclusive. */
export function dayColumns(rangeStart: Dayjs, rangeEnd: Dayjs): Dayjs[] {
  const cols: Dayjs[] = [];
  let cur = rangeStart.startOf('day');
  const end = rangeEnd.startOf('day');
  while (!cur.isAfter(end)) {
    cols.push(cur);
    cur = cur.add(1, 'day');
  }
  return cols;
}

export function isWeekend(d: Dayjs): boolean {
  const day = d.day();
  return day === 0 || day === 6;
}

/** True when the order/section is locked from editing per the backend workflow. */
export function isLocked(status?: string): boolean {
  return status === 'DONE' || status === 'CANCEL';
}

/** Past its end date but not completed/cancelled → overdue. */
export function isOverdue(end: string | null | undefined, status?: string): boolean {
  if (!end || status === 'DONE' || status === 'CANCEL') return false;
  return dayjs(end).startOf('day').isBefore(dayjs().startOf('day'));
}

/**
 * Keys (`${orderId}:${sectionIndex}`) of sections that overlap another section in the
 * same lane (section master) — i.e. capacity conflicts. Shared by the board (outline)
 * and the page (summary count).
 */
export function sectionConflictKeys(orders: ProductionOrder[]): Set<string> {
  const lanes = new Map<
    string,
    { orderId: string; idx: number; start: string; end: string }[]
  >();
  orders.forEach((o) =>
    o.sections?.forEach((s, idx) => {
      if (!s.productionStart || !s.productionEnd) return;
      const key = s.section?.code ?? s.section?.id ?? 'section';
      const arr = lanes.get(key) ?? [];
      arr.push({ orderId: o.id, idx, start: s.productionStart, end: s.productionEnd });
      lanes.set(key, arr);
    }),
  );
  const conflicts = new Set<string>();
  lanes.forEach((arr) => {
    const sorted = arr.sort(
      (a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf(),
    );
    for (let i = 1; i < sorted.length; i++) {
      if (dayjs(sorted[i].start).isBefore(dayjs(sorted[i - 1].end).add(1, 'day'))) {
        conflicts.add(`${sorted[i].orderId}:${sorted[i].idx}`);
        conflicts.add(`${sorted[i - 1].orderId}:${sorted[i - 1].idx}`);
      }
    }
  });
  return conflicts;
}

/** Count of orders that are overdue (order-level or any section overdue). */
export function countOverdueOrders(orders: ProductionOrder[]): number {
  return orders.filter(
    (o) =>
      isOverdue(o.productionEnd, o.status) ||
      o.sections?.some((s) => isOverdue(s.productionEnd, s.status)),
  ).length;
}
